import React, { useEffect, useState } from 'react'
import {
  useAllValues,
  useFieldCurrencyMap,
  useFieldValidationError,
  useProcessContext,
  useReviewValues,
  useSelectedTableData,
  useTableSequenceAndHeaders,
  useTableStructure,
} from '../../../store'
import {
  FIELD_TYPES,
  CURRENCY_COUNTRY_CODE,
  TABLE_CONTEXTS,
  COMPUTED_OPERATION_TYPES,
} from '../../../utility/constants'
import AutoCompleteField from '../Fields/AutoCompleteField'
import MultiSelectField from '../Fields/MultiSelectField'
import DateAndDateTime from '../Fields/DateAndDateTime'
import TextOrNumberField from '../Fields/TextOrNumberField'
import BigTextField from '../Fields/BigTextField'
import CurrencyField from '../Fields/CurrencyField'
import FileUploadField from '../Fields/FileUploadField'
import FilePreviewField from '../Fields/FilePreviewField'
import { listDataService } from '../../../services/listDataService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { getFieldId, getLegacySubfieldId, getTableFieldId } from '../utils/utils'
import { DROPDOWN_DEFAULT_VALUE } from '../utils/autoCompute'
import { fileServices } from '../../../services/fileServices'
import { checkFieldConstraints, getDefaultValueForField } from '../utils/fieldUtil'
import {
  syncChild,
  syncWithParent,
  syncChildForListMulti,
  syncChildRecursively,
} from '../utils/constraintUtils'
import moment from 'moment'
import { processDataService } from '../../../services/processDataService'
import TableField from '../Fields/TableField/TableField'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { currencyFieldService } from '../../../services/currencyFieldService'
import _ from 'lodash'
import { getTimeInMillis } from '../../../utility/datetime'
import CustomView from '../../../custom_views/CustomView'
import {
  getDefaultValueAsPerDBDefaultValue,
  getSubfieldDefaultValue,
} from '../Fields/TableField/tableUtil'
import { convertFromLegacyToRowWiseData } from '../utils/tableFieldUtil'
import { computeCurrency } from '../utils/currency'
import { constraintValidationWithOperation } from '../utils/validationUtil'
import TimeStampedBigTextField from '../Fields/TimeStampedBigTextField'
import { indexedDbStorage } from '../../../utility/indexedDbStorage'
import { applyUserFilter } from '../../../utility/dataEntry/formData'
import { autoComputeFieldValueOptimized } from '../utils/autoComputeOptimized'

const clampNumericString = (input, places) => {
  if (input === '' || input === null || input === undefined) return ''
  const str = String(input)
  const match = str.match(/^(-?\d+)(?:\.(\d*))?/)
  if (!match) return ''
  const [, intPart, fracPart = ''] = match
  if (places === null || places === undefined) return match[0]
  if (places === 0) return intPart
  const clampedFrac = fracPart.slice(0, places)
  return clampedFrac.length ? `${intPart}.${clampedFrac}` : intPart
}

// Session cache to prevent repeated API calls per list field while avoiding stale data from indexedDb
const pivotRestrictionsCache = {}

const Fields = ({ field, fields, disabled = false, helperText = null, inputSx }) => {
  /* center store values */
  const { user } = useAuthContext()
  const { allValues, setAllValues, getAllValues, setInitialAllValues } = useAllValues()

  const listDataIdMetadataMap = useProcessContext(
    (state) => state.processContext.listDataIdMetadataMap,
  )

  const fieldTemplateDep = useProcessContext((state) => state.processContext.fieldTemplateDep)
  const userTeamMap = useProcessContext((state) => state.processContext.userTeamMap)
  const listDataDep = useProcessContext((state) => state.processContext.listDataDep)
  const fieldConstraints = useProcessContext((state) => state.processContext.fieldConstraints)
  const tableStructure = useTableStructure((state) => state.tableStructure)
  const teamsList = useProcessContext((state) => state.processContext.dynamicListData.teamsList)

  /* field validation error */
  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)
  const setFieldValidationError = useFieldValidationError((state) => state.setFieldValidationError)

  /* process context */
  const processContext = useProcessContext((state) => state.processContext)
  const setProcessContext = useProcessContext((state) => state.setProcessContext)
  const selectedTableData = useSelectedTableData((state) => state.selectedTableData)
  const updateSelectedTableRow = useSelectedTableData((state) => state.updateSelectedTableRow)

  /* local states */
  const [listData, setListData] = useState([])
  const [listDataSynced, setListDataSynced] = useState([])
  const [importDataLoading, setImportDataLoading] = useState(false)

  const conditionalEnableDisable = useProcessContext(
    (state) => state.processContext.conditionalEnableDisable,
  )
  const tableSubfieldsMap = processContext?.tableSubfieldsMap

  /* field currency map */
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)
  const setFieldCurrencyMap = useFieldCurrencyMap((state) => state.setFieldCurrencyMap)
  const defaultCurrency = processContext?.companyDetails?.default_currency_code

  /* Table sequence and headers */
  const setTableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.setTableSequenceAndHeaders,
  )
  const tableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.tableSequenceAndHeaders,
  )

  useEffect(() => {
    if (processContext?.processData?.activity_template_id) {
      ;(async () => {
        /* get all list data - TODO: need to cache the result - listData*/
        if (
          field.data_structure === FIELD_TYPES.LIST ||
          field.data_structure === FIELD_TYPES.LIST_MULTI
        ) {
          const atId = processContext?.processData?.activity_template_id

          if (!pivotRestrictionsCache[atId]) {
            pivotRestrictionsCache[atId] = fieldTemplateService
              .getPivotFieldRestrictions(user, atId)
              .then((res) => res.data)
              .catch((err) => {
                console.error('Failed to fetch pivot field restrictions', err)
                delete pivotRestrictionsCache[atId]
                return []
              })
          }

          let pivotFieldRestrictions = await pivotRestrictionsCache[atId]

          const pivotFieldRestriction = pivotFieldRestrictions?.find(
            (it) => it.pivot_field_template_id == field.id,
          )

          if (pivotFieldRestriction) {
            const restrictedData = await processDataService.getPivotFieldFilterData(user, field.id)
            const listData = await getListData()

            const restrictedDataModified = restrictedData?.data?.map(
              (it) => it.process_reference_num + '/' + it.process_template_id,
            )

            const filterDataAfterRestriction = listData?.filter((it) =>
              restrictedDataModified?.includes(
                it?.metadata?.process_reference_num + '/' + it?.metadata?.process_template_id,
              ),
            )
            setListData(filterDataAfterRestriction)
          } else {
            if (field.list_id == null) {
              const meta = field.dynamic_field_type

              const allData = processContext.dynamicListData
              if (meta?.type == 'teams') {
                let listData = []

                if (meta?.supertype) {
                  listData = allData?.teamsList
                    ?.filter((it) => meta.supertype.includes(it.metadata.team_subtype))
                    ?.filter((it) => meta?.subtypes.includes(it.metadata.team_type))
                } else {
                  listData = allData?.teamsList?.filter(
                    (it) =>
                      meta?.subtypes?.includes(it.metadata.team_type) &&
                      it.metadata.team_subtype == null,
                  )
                }

                setListData(listData)
              } else if (meta?.type == 'users') {
                setListData(applyUserFilter(allData?.usersList || [], meta))
              } else {
                console.error('Dynamic Field Implementation Pending for ' + meta?.type)
              }
            } else {
              const listData = await getListData()
              setListData(listData)
            }
          }
        }
      })()
    }
  }, [processContext?.processData?.activity_template_id, processContext?.dynamicListData, field])

  /* sync list items with parent field value */
  useEffect(() => {
    const isChildFieldsThere = fieldTemplateDep?.find((it) =>
      fields.find((field) => field.id == it.child_field_template_id),
    )

    if (isChildFieldsThere) {
      const items = syncWithParent(
        field.id,
        fieldTemplateDep,
        listDataDep,
        allValues,
        listData,
        teamsList,
        {
          userTeamMap,
          field,
          selectedValues,
        },
      )

      setListDataSynced(items)
    } else {
      setListDataSynced(listData)
    }
  }, [allValues, listData, teamsList, fields])

  const getListData = async () => {
    const cacheKey = `list_data_${field.list_id}`

    // let listData = getDataFromCache(cacheKey)
    let listData = await indexedDbStorage.getItem(cacheKey)

    if (!listData || field.is_pivot_field) {
      listData = (await listDataService.getData(user, field.list_id)).data
      await indexedDbStorage.setItem(cacheKey, listData)
    }

    if (field.is_pivot_field) {
      return listData?.filter((it) =>
        field?.import_from_process_template_id
          ?.split(',')
          .includes(it?.metadata?.process_template_id + ''),
      )
    } else {
      return listData
    }
  }

  const triggerAutoCompute = async (allValues, listDataIdMetadataMap, processContext) => {
    const ctx = {
      processPrefix: processContext?.processTemplate?.prefix,
      processReferenceNum: processContext?.processData?.process_reference_num,
      processTemplateId: processContext?.processData?.process_template_id,
      version: processContext?.processData?.version,
    }

    const idFieldMap = {}
    processContext.allFields?.forEach((cur) => {
      idFieldMap[cur.id] = cur
    })

    const computedValues = await autoComputeFieldValueOptimized({
      allActivitiesCreatedAtWithVersion: processContext?.allActivitiesCreatedAtWithVersion,
      allProcessData: processContext?.allProcessData,
      context: ctx,
      dynamicListData: processContext?.dynamicListData,
      holidayList: processContext?.holidayList,
      listDataIdMetadataMap: listDataIdMetadataMap,
      selectedTableData: selectedTableData,
      tableRowIndex: null,
      tableSubfieldsMap: processContext?.tableSubfieldsMap,
      user: user,
      computeFields: processContext.allFields.filter((it) => it.is_computed && !it.is_subfield),
      data: { allValues, rowValues: {} },
      idFieldMap,
    })

    return computedValues
  }

  const applyPivotFieldChange = async (fieldId, value) => {
    if (processContext?.processData && value?.id !== undefined) {
      try {
        const interProcessData = await processDataService.getInterProcessData(
          user,
          fieldId,
          processContext?.processData?.id,
          value?.id,
        )

        return interProcessData?.data
      } catch (error) {
        console.error('applyPivotFieldChange', error)
      }
    }
    return {}
  }

  const normalizeImport = (allValues, importedData, pivotFieldTemplateId, tableSubfields = []) => {
    // get all key which needs to be removed from all value
    const allFieldKeyWithoutRowNumber = (importedData?.currentActivityImportingFields || [])
      ?.filter(
        (field) => field?.is_subfield && field?.pivot_field_template_id == pivotFieldTemplateId,
      )
      .map((field) => getFieldId(field.id))

    const importingTables = (importedData?.currentActivityImportingFields || [])
      ?.filter(
        (field) =>
          field?.data_structure == FIELD_TYPES.TABLE &&
          field?.pivot_field_template_id == pivotFieldTemplateId,
      )
      .map((field) => field.id)

       const importingTableIds = Array.from(
      new Set([
        ...importingTables,
        ...(tableSubfields || [])
          .filter((table) =>
            table?.subfields?.some(
              (subfield) =>
                subfield?.is_imported_field &&
                subfield?.pivot_field_template_id == pivotFieldTemplateId,
            ),
          )
          .map((table) => table.id),
      ]),
    )

    const importingTableKeys = importingTableIds.map((tableId) => getTableFieldId(tableId))

    const importingSelectableTable = (importedData?.currentActivityImportingFields || [])?.filter(
      (field) =>
        field?.data_structure == FIELD_TYPES.TABLE &&
        field.is_selectable_table &&
        field?.pivot_field_template_id == pivotFieldTemplateId,
    )

    // removing previously selected rows
    if (importingSelectableTable?.length > 0) {
      importingSelectableTable.forEach((field) => {
        updateSelectedTableRow(field.id, [])
      })
    }

    let nonImportableColumnInImportableTableValues = {}
    let nonImportableColumnInImportableTableWithoutRowNumber = []

    const tableSequence = importedData?.tableSequenceAndHeaders || []

    tableSubfields?.forEach((table) => {
      const tableId = table.id
      let columns = table.subfields

      if (importingTableIds?.includes(tableId)) {
        const rowsBeingImported = tableSequence
          ?.find((it) => it.table_field_template_id == tableId)
          ?.sequence_header_map?.map((it) => it.row)

        let nonImportingColumn = columns?.filter((field) => !field.is_imported_field)

        // set default value to non importing fields
        if (nonImportingColumn.length > 0) {
          nonImportingColumn.forEach((column) => {
            // get all field keys without row num
            nonImportableColumnInImportableTableWithoutRowNumber.push(getFieldId(column.id))

            rowsBeingImported?.forEach((row) => {
              nonImportableColumnInImportableTableValues[getLegacySubfieldId(column.id, row)] =
                getDefaultValueForField(column)
            })
          })
        }
      }
    })

    // now remove all keys from allValues
    const finalAllValues = Object.entries(allValues || {}).reduce((acc, [key, value]) => {
      if (
        allFieldKeyWithoutRowNumber.includes(key.split('__')[0]) ||
        nonImportableColumnInImportableTableWithoutRowNumber.includes(key.split('__')[0]) ||
        importingTableKeys.includes(key)
      ) {
        return acc
      } else {
        return { ...acc, [key]: value }
      }
    }, {})

   const legacyValuesAfterImport = {
      ...finalAllValues,
      ...importedData?.importData,
      ...nonImportableColumnInImportableTableValues,
    }

    const importedTableRowWiseValues = importingTableIds.reduce((acc, tableId) => {
      const table = tableSubfields?.find((table) => table.id == tableId)
      if (!table?.subfields?.length) return acc

      return {
        ...acc,
        ...convertFromLegacyToRowWiseData(tableId, legacyValuesAfterImport, table.subfields),
      }
    }, {})

    return {
      ...legacyValuesAfterImport,
      ...importedTableRowWiseValues,
    }
  }

  const getAdjustedValueBasedOnEnableDisable = (
    conditionalEnableDisable,
    field,
    allValueUpdated,
    tableSubfieldsMap,
  ) => {
    let copy = _.cloneDeep(allValueUpdated)

    const conditions = conditionalEnableDisable.filter(
      (condition) => condition.source_field_template_id === field.id,
    )

    for (const condition of conditions) {
      // NF -> TF
      if (condition.destination_data_structure === FIELD_TYPES.TABLE) {
        const isEnabled = constraintValidationWithOperation(
          allValueUpdated[getFieldId(field.id)],
          condition.enable_on_value,
          condition.operation,
        )
        if (isEnabled) {
          const subfields = tableSubfieldsMap.filter(
            (it) => it.table_field_template_id === condition.destination_field_template_id,
          )
          const { data: tableKeysAsPerDefault, sequence } = getDefaultValueAsPerDBDefaultValue(
            condition.destination_field_template_id,
            subfields,
          )

          // storing updated sequence and header to store
          let updatedTableSequenceAndHeaders = tableSequenceAndHeaders.map((it) => {
            if (it.table_field_template_id == condition.destination_field_template_id) {
              return {
                ...it,
                sequence_header_map: sequence,
              }
            }
            return it
          })
          setTableSequenceAndHeaders(updatedTableSequenceAndHeaders)
          // update the table header sequence to server db
          ;(async () => {
            await fieldTemplateService.updateTableSequenceAndHeaders(user, {
              process_id: processContext.processData.id,
              table_field_template_id: condition.destination_field_template_id,
              table_sequence_and_headers: sequence,
            })
          })()

          copy = { ...copy, ...tableKeysAsPerDefault }
        } else {
          tableSubfieldsMap.forEach((subfield) => {
            if (subfield.table_field_template_id === condition.destination_field_template_id) {
              Object.keys(copy).forEach((key) => {
                if (
                  key.startsWith(`${getFieldId(subfield.subfield_template_id)}__`) ||
                  key.startsWith(getTableFieldId(subfield.table_field_template_id))
                ) {
                  delete copy[key]
                }
              })
              copy[`${getFieldId(subfield.subfield_template_id)}__1`] = ''
              copy[`${getFieldId(subfield.table_field_template_id)}`] = 1 //default Table Value after Enable the field
            }
          })
        }
      } else if (condition.is_destination_field_subfield && !condition.is_source_field_subfield) {
        // NF -> SF (Column)
        const isEnabled = constraintValidationWithOperation(
          allValueUpdated[getFieldId(field.id)],
          condition.enable_on_value,
          condition.operation,
        )

        if (isEnabled) {
          // set column value to db default for entire rows if available
          Object.keys(copy).forEach((key) => {
            if (key.startsWith(`${getFieldId(condition.destination_field_template_id)}__`)) {
              const rowNum = key.split('__')[1]
              copy[getLegacySubfieldId(condition.destination_field_template_id, rowNum)] =
                getSubfieldDefaultValue(
                  {
                    data_structure: condition.destination_data_structure,
                    default_value: condition.destination_field_default_value,
                  },
                  Number(rowNum) - 1,
                )
            }
          })

          // do same for tabular data
          const tableFieldTemplateId = tableSubfieldsMap.find(
            (it) => condition.destination_field_template_id === it.subfield_template_id,
          )?.table_field_template_id

          // check if tabular data is there if yes update
          if (copy[getTableFieldId(tableFieldTemplateId)]) {
            Object.keys(copy[getTableFieldId(tableFieldTemplateId)]).forEach((trKey) => {
              const tr = copy[getTableFieldId(tableFieldTemplateId)][trKey]

              // find the column and update with db default value
              Object.keys(tr).forEach((columnKey) => {
                if (columnKey.startsWith(getFieldId(condition.destination_field_template_id))) {
                  tr[columnKey] = getSubfieldDefaultValue(
                    {
                      data_structure: condition.destination_data_structure,
                      default_value: condition.destination_field_default_value,
                    },
                    Number(trKey.split('_')[1]) - 1,
                  )
                }
              })

              copy[getTableFieldId(tableFieldTemplateId)][trKey] = tr
            })
          }
        } else {
          const { destination_field_template_id } = condition
          tableSubfieldsMap.forEach((mapping) => {
            if (mapping.subfield_template_id === destination_field_template_id) {
              const tableFieldTemplateId = mapping.table_field_template_id
              // set column value to system default for entire rows
              Object.keys(copy).forEach((key) => {
                if (key.startsWith(`${getFieldId(destination_field_template_id)}__`)) {
                  const rowNum = key.split('__')[1]
                  copy[getLegacySubfieldId(destination_field_template_id, rowNum)] =
                    getDefaultValueForField({
                      data_structure: condition.destination_data_structure,
                    })
                }
              })

              // set column value to system default for entire rows for tabular data
              if (copy[getTableFieldId(tableFieldTemplateId)]) {
                Object.keys(copy[getTableFieldId(tableFieldTemplateId)]).forEach((trKey) => {
                  const tr = copy[getTableFieldId(tableFieldTemplateId)][trKey]
                  Object.keys(tr).forEach((subKey) => {
                    if (subKey.startsWith(getFieldId(destination_field_template_id))) {
                      tr[subKey] = getDefaultValueForField({
                        data_structure: condition.destination_data_structure,
                      })
                    }
                  })
                })
              }
            }
          })
        }
      } else {
        const isEnabled = constraintValidationWithOperation(
          allValueUpdated[getFieldId(field.id)],
          condition.enable_on_value,
          condition.operation,
        )

        if (isEnabled) {
          const destinationField = {
            data_structure: condition.destination_data_structure,
            default_value: condition.destination_field_default_value,
          }
          if (
            !condition.is_destination_field_subfield &&
            condition.destination_data_structure != FIELD_TYPES.TABLE
          ) {
            copy[getFieldId(condition.destination_field_template_id)] =
              condition.destination_field_default_value &&
              condition.destination_field_default_value?.values?.length > 0
                ? condition.destination_field_default_value?.values[0]
                : getDefaultValueForField(destinationField)
          }
        } else {
          const destinationField = {
            data_structure: condition.destination_data_structure,
            default_value: condition.destination_field_default_value,
          }
          if (
            !condition.is_destination_field_subfield &&
            condition.destination_data_structure != FIELD_TYPES.TABLE
          ) {
            copy[getFieldId(condition.destination_field_template_id)] =
              getDefaultValueForField(destinationField)
          }
        }
      }
    }

    return copy
  }

  const onChange = async (e, v) => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        if (field.is_pivot_field) {
          // Step 1: Update value data
          let allValueUpdated = {
            ...getAllValues(),
            [getFieldId(field?.id)]: v?.id || DROPDOWN_DEFAULT_VALUE,
          }

          setImportDataLoading(true)

          // Step 2: Get imported data on pivot field change
          const importedFieldValue = await applyPivotFieldChange(field.id, v)

          try {
            const currentCtx = useProcessContext.getState().processContext
            const pd = currentCtx?.processData || processContext?.processData
            const prefetchActivityTemplateId =
              currentCtx?.activityTemplate?.id || pd?.activity_template_id

            if (
              prefetchActivityTemplateId &&
              pd?.id &&
              pd?.process_reference_num &&
              pd?.process_template_id
            ) {
              const prefetchResult = await listDataService.getPlaceholderPrefetch(
                user,
                prefetchActivityTemplateId,
                pd.id,
                pd.process_reference_num,
                pd.process_template_id,
                allValueUpdated,
              )
              const latestCtx = useProcessContext.getState().processContext
              setProcessContext({
                ...latestCtx,
                externalValues: prefetchResult?.externalValues ?? {},
                fieldMappings: prefetchResult?.fieldMappings ?? {},
                fieldMetadata: prefetchResult?.fieldMetadata ?? latestCtx?.fieldMetadata ?? {},
                placeholderPrefetchUpdatedAt: getTimeInMillis(),
              })
            }
          } catch (err) {
            console.error('Error refreshing placeholders after pivot change:', err)
          }

          // Step 3: Normalize values and update
          const normalizedValuesAfterImporting = normalizeImport(
            allValueUpdated,
            importedFieldValue,
            field.id,
            processContext?.tableSubfields,
          )
          allValueUpdated = normalizedValuesAfterImporting

          // Step 4: Auto-compute computed fields
          const autoComputeFieldValues = await triggerAutoCompute(
            allValueUpdated,
            listDataIdMetadataMap,
            processContext,
          )

          allValueUpdated = { ...allValueUpdated, ...autoComputeFieldValues }

          let computedValuesOfSumAcrossProcess = {}

          if (v?.id !== undefined) {
            // Step 5: Handle currency imports
            const allNonDefaultCurrenciesImported =
              await currencyFieldService.getAllNonDefaultCurrenciesImported(
                user,
                v?.metadata?.process_template_id,
                v?.metadata?.process_reference_num,
              )

            const importingCurrencyFields =
              importedFieldValue?.currentActivityImportingFields?.filter(
                (it) => it.is_imported_field && it.data_structure === FIELD_TYPES.CURRENCY,
              )

            await currencyFieldService.deleteAllNonDefaultCurrencies(
              user,
              processContext?.processData?.id,
            )

            let updatedCurrencyMap = {}

            if (importingCurrencyFields?.length > 0) {
              for (const field of importingCurrencyFields) {
                if (allNonDefaultCurrenciesImported.length > 0) {
                  for (const datum of allNonDefaultCurrenciesImported) {
                    if (
                      field.imported_from_field_template_id &&
                      field.imported_from_field_template_id
                        .split(',')
                        .map((id) => id.trim())
                        .includes(datum.field_template_id.toString())
                    ) {
                      updatedCurrencyMap = {
                        ...updatedCurrencyMap,
                        [field.id]: CURRENCY_COUNTRY_CODE[datum.currency_code],
                      }
                    }
                  }
                } else {
                  updatedCurrencyMap = {
                    ...updatedCurrencyMap,
                    [field.id]: CURRENCY_COUNTRY_CODE[defaultCurrency],
                  }
                }
              }
            }

            // Step 6: Update table sequence headers if present
            if (importedFieldValue?.tableSequenceAndHeaders?.length > 0) {
              const updatedTableSequenceAndHeaders = tableSequenceAndHeaders.map((it) => {
                const updatedSequence = importedFieldValue?.tableSequenceAndHeaders?.find(
                 (ift) => ift.table_field_template_id == it.table_field_template_id,
                )
                if (updatedSequence) {
                  return {
                    ...it,
                    sequence_header_map: updatedSequence.sequence_header_map,
                  }
                }
                return it
              })

              setTableSequenceAndHeaders(updatedTableSequenceAndHeaders)
              await fieldTemplateService.updateMultipleTableSequenceAndHeaders(user, {
                tableSequenceAndHeaders: importedFieldValue?.tableSequenceAndHeaders || [],
              })
            }

            // Step 7: Compute currencies after import
            const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
              { ...updatedCurrencyMap, ...fieldCurrencyMap },
              processContext?.allFields || [],
              defaultCurrency,
              allValueUpdated,
            )

            let fcMap = {
              ...updatedFieldCurrencyMap,
              ...nonDefaultValues.reduce((acc, cur) => {
                return { ...acc, [cur.id]: cur.currency }
              }, {}),
            }

            // Step 8: Get sum across process computation
            const { computedSumAcrossProcess, ndc } = (
              await processDataService.getSumAcrossProcessComputation(
                user,
                processContext?.processData?.id,
                { list_data_id: v?.id, field_template_id: field.id },
              )
            ).data

            // Step 9: Update sum across currency map
            const fcMapUpdated = {
              ...fcMap,
              ...Object.entries(ndc).reduce((acc, [key, value]) => {
                return { ...acc, [key]: CURRENCY_COUNTRY_CODE[value] }
              }, {}),
            }

            const { updatedFieldCurrencyMap: updatedFieldCurrencyMap2 } = computeCurrency(
              fcMapUpdated,
              processContext?.allFields || [],
              defaultCurrency,
              allValueUpdated,
            )

            for (let [key, value] of Object.entries(updatedFieldCurrencyMap2)) {
              const currencyCode = Object.entries(CURRENCY_COUNTRY_CODE).find(
                ([, val]) => value === val,
              )
              if (currencyCode && currencyCode[0] !== defaultCurrency) {
                await currencyFieldService.addNonDefaultCurrency(user, {
                  field_template_id: key,
                  currency_code: currencyCode[0],
                  process_id: processContext?.processData?.id,
                })
              }
            }

            computedValuesOfSumAcrossProcess = computedSumAcrossProcess
            setFieldCurrencyMap(updatedFieldCurrencyMap2)
          }

          // Step 10: Finalize values
          const allValues = {
            ...allValueUpdated,
            ...computedValuesOfSumAcrossProcess,
          }
          setInitialAllValues(allValues)

          setImportDataLoading(false)
        } else {
          /* update value data */
          let allValueUpdated = {
            ...getAllValues(),
            [getFieldId(field.id)]: v?.id || DROPDOWN_DEFAULT_VALUE,
          }

          // adjust value as per conditional enable disable
          allValueUpdated = getAdjustedValueBasedOnEnableDisable(
            conditionalEnableDisable,
            field,
            allValueUpdated,
            tableSubfieldsMap,
          )
          /* sync child to maintain parent-child relationship */
          allValueUpdated = syncChild(
            field.id,
            fieldTemplateDep,
            listDataDep,
            allValueUpdated,
            teamsList,
            {
              allValueUpdated,
              tableStructure,
              userTeamMap,
            },
          )

          // validate descendants: keep if valid, reset if invalid
          allValueUpdated = syncChildRecursively(
            fields,
            fieldTemplateDep,
            listDataDep,
            userTeamMap,
            allValueUpdated,
          )

          /* for first level auto-compute -> trigger auto compute computed fields */
          const autoComputeFieldValues1 = await triggerAutoCompute(
            allValueUpdated,
            listDataIdMetadataMap,
            processContext,
          )
          /* for 2nd level auto-compute -> trigger auto compute computed fields */
          const autoComputeFieldValues = await triggerAutoCompute(
            { ...allValueUpdated, ...autoComputeFieldValues1 },
            listDataIdMetadataMap,
            processContext,
          )

          const isSumAcrossProcessActionField = processContext?.allFields?.find(
            (it) =>
              it.is_computed &&
              it.metadata?.operation === COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS &&
              it.metadata?.pivotFieldTemplateId == field.id,
          )

          let updatedAllValues = {
            [getFieldId(field.id)]: v?.id,
            ...autoComputeFieldValues,
          }
          if (isSumAcrossProcessActionField) {
            const { computedSumAcrossProcess, ndc } = (
              await processDataService.getSumAcrossProcessComputation(
                user,
                processContext?.processData?.id,
                { list_data_id: v?.id, field_template_id: field.id },
              )
            ).data

            updatedAllValues = {
              ...updatedAllValues,
              ...computedSumAcrossProcess,
            }

            const fcMapUpdated = {
              ...fieldCurrencyMap,
              ...Object.entries(ndc).reduce((acc, [key, value]) => {
                return { ...acc, [key]: CURRENCY_COUNTRY_CODE[value] }
              }, {}),
            }

            const { updatedFieldCurrencyMap: updatedFieldCurrencyMap2 } = computeCurrency(
              fcMapUpdated,
              processContext?.allFields || [],
              defaultCurrency,
              { ...allValueUpdated, ...updatedAllValues },
            )

            for (let [key, value] of Object.entries(updatedFieldCurrencyMap2)) {
              const currencyCode = Object.entries(CURRENCY_COUNTRY_CODE).find(
                ([, val]) => value === val,
              )
              if (currencyCode && currencyCode[0] !== defaultCurrency) {
                await currencyFieldService.addNonDefaultCurrency(user, {
                  field_template_id: key,
                  currency_code: currencyCode[0],
                  process_id: processContext?.processData?.id,
                })
              }
            }
            setFieldCurrencyMap(updatedFieldCurrencyMap2)
          }

          /* persist changes */
          persistAllValueChange(updatedAllValues, allValueUpdated)
        }
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(
            field,
            v?.id || DROPDOWN_DEFAULT_VALUE,
            fieldConstraints,
            getAllValues(),
          ),
        })
        break
      case FIELD_TYPES.LIST_MULTI:
        {
          const targetVals = getTargetValues(v)
          const baseAllValues = { ...getAllValues(), [getFieldId(field.id)]: targetVals }

          // validate immediate child list-multi relationships
          const updatedChildData = syncChildForListMulti({
            allValues: baseAllValues,
            fieldId: field.id,
            targetValue: targetVals,
            listDataDep,
            fieldTemplateDep,
          })

          // then validate recursively for deeper descendants
          const afterImmediate = { ...baseAllValues, ...updatedChildData }
          const validatedAllValues = syncChildRecursively(
            fields,
            fieldTemplateDep,
            listDataDep,
            userTeamMap,
            afterImmediate,
          )

          // persist minimal changes with full snapshot so resets apply
          persistAllValueChange(
            { [getFieldId(field.id)]: targetVals, ...updatedChildData },
            validatedAllValues,
          )
        }
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(field, getTargetValues(v), fieldConstraints, getAllValues()),
        })
        break
      case FIELD_TYPES.FILE:
        const file = e.target.files[0]
        const result = (await fileServices.uploadFile(user, file)).data
        // adjust value as per conditional enable disable
        let allValueAfterConditionalEnable = getAdjustedValueBasedOnEnableDisable(
          conditionalEnableDisable,
          field,
          getAllValues(),
          tableSubfieldsMap,
        )
        /* persist changes */
        persistAllValueChange(
          { [getFieldId(field.id)]: result?.filePath?.split('/')[1] || '' },
          allValueAfterConditionalEnable,
        )
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(
            field,
            result?.filePath?.split('/')[1],
            fieldConstraints,
            getAllValues(),
          ),
        })
        break
      case FIELD_TYPES.CURRENCY:
        if (e.floatValue !== undefined) {
          let allValueUpdated = {
            ...getAllValues(),
            [getFieldId(field.id)]: e.floatValue,
          }
          // compute currency
          const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
            fieldCurrencyMap,
            fields,
            defaultCurrency,
            { ...allValues, [getFieldId(field.id)]: e.floatValue },
          )

          let map = {
            ...updatedFieldCurrencyMap,
            ...nonDefaultValues.reduce((acc, cur) => {
              return { ...acc, [cur.id]: cur.currency }
            }, {}),
          }
          setFieldCurrencyMap(map)
          // adjust value as per conditional enable disable
          allValueUpdated = getAdjustedValueBasedOnEnableDisable(
            conditionalEnableDisable,
            field,
            allValueUpdated,
            tableSubfieldsMap,
          )
          /* for first level auto-compute -> trigger auto compute computed fields */
          const autoComputeFieldValues1 = await triggerAutoCompute(
            allValueUpdated,
            listDataIdMetadataMap,
            processContext,
          )
          /* for 2nd level auto-compute -> trigger auto compute computed fields */
          const autoComputeFieldValues = await triggerAutoCompute(
            { ...allValueUpdated, ...autoComputeFieldValues1 },
            listDataIdMetadataMap,
            processContext,
          )
          /* persist changes */
          persistAllValueChange(
            { [getFieldId(field.id)]: e.floatValue, ...autoComputeFieldValues },
            allValueUpdated,
          )
        } else {
          persistAllValueChange({ [getFieldId(field.id)]: 0 })
        }
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(field, e.floatValue, fieldConstraints, getAllValues()),
        })
        break
      case FIELD_TYPES.BIGTEXT:
      case FIELD_TYPES.TEXT:
        // adjust value as per conditional enable disable
        let allValueAfterConditionalEnableText = getAdjustedValueBasedOnEnableDisable(
          conditionalEnableDisable,
          field,
          { ...getAllValues(), [getFieldId(field.id)]: e.target.value },
          tableSubfieldsMap,
        )
        /* persist changes */
        persistAllValueChange(
          { [getFieldId(field.id)]: e.target.value },
          allValueAfterConditionalEnableText,
        )
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(field, e.target.value, fieldConstraints, getAllValues()),
        })
        break
      case FIELD_TYPES.NUMBER:
        {
          const raw = e.target.value
          const preserveLeadingZeros = true

          const places =
            typeof field?.num_decimal_places == 'number'
              ? field.num_decimal_places
              : field?.num_decimal_places == 0
                ? 0
                : null

          const persistAndValidate = async (persistedValue, validationValue = persistedValue) => {
            let allValueUpdated = {
              ...getAllValues(),
              [getFieldId(field.id)]: persistedValue,
            }
            allValueUpdated = getAdjustedValueBasedOnEnableDisable(
              conditionalEnableDisable,
              field,
              allValueUpdated,
              tableSubfieldsMap,
            )
            const autoComputeFieldValues1 = await triggerAutoCompute(
              allValueUpdated,
              listDataIdMetadataMap,
              processContext,
            )
            const autoComputeFieldValues = await triggerAutoCompute(
              { ...allValueUpdated, ...autoComputeFieldValues1 },
              listDataIdMetadataMap,
              processContext,
            )
            persistAllValueChange(
              { ...autoComputeFieldValues, [getFieldId(field.id)]: persistedValue },
              allValueUpdated,
            )
            setFieldValidationError({
              ...fieldValidationError,
              ...checkFieldConstraints(field, validationValue, fieldConstraints, allValueUpdated),
            })
          }

          if (raw === '' || raw === null || raw === undefined) {
            await persistAndValidate('')
            break
          }

          if (preserveLeadingZeros) {
            const clampedString = clampNumericString(raw, places)
            await persistAndValidate(clampedString, clampedString)
            break
          }

          const clampedNumericString = clampNumericString(raw, places)
          let num = Number.parseFloat(clampedNumericString)
          if (!Number.isFinite(num)) num = 0

          await persistAndValidate(num, num)
        }
        break
      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        /* persist changes */
        persistAllValueChange({
          [getFieldId(field.id)]: e?.format('YYYY-MM-DD HH:mm:ss'),
        })
        /* apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(field, e, fieldConstraints, getAllValues()),
        })
        break
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        // Adjust value based on conditional enable/disable logic
        let allValueAfterConditionalEnableTimestampedBigText = getAdjustedValueBasedOnEnableDisable(
          conditionalEnableDisable,
          field,
          { ...getAllValues(), [getFieldId(field.id)]: e.target.value },
          tableSubfieldsMap,
        )

        /* Persist changes */
        persistAllValueChange(
          { [getFieldId(field.id)]: e.target.value },
          allValueAfterConditionalEnableTimestampedBigText,
        )

        /* Apply field validation */
        setFieldValidationError({
          ...fieldValidationError,
          ...checkFieldConstraints(field, e.target.value, fieldConstraints, getAllValues()),
        })
        break
      default:
        console.log(`onChange - ${field.data_structure} is not supported!`)
    }
  }

  const persistAllValueChange = (updatedAllValues, allValueUpdated) => {
    // If caller provided a full updated snapshot (e.g., after clearing descendants),
    // commit via setInitialAllValues so deletions are applied.
    if (allValueUpdated) {
      setInitialAllValues({ ...allValueUpdated, ...updatedAllValues, updatedAt: getTimeInMillis() })
      return
    }

    // Fallback to previous behavior
    let destinationFields = processContext?.conditionalEnableDisable?.filter(
      (it) =>
        it.source_field_template_id == field.id && [getFieldId(field.id)] != it.enable_on_value,
    )

    if (destinationFields.length > 0) {
      setInitialAllValues({ ...getAllValues(), ...updatedAllValues, updatedAt: getTimeInMillis() })
    } else {
      setAllValues(updatedAllValues)
    }
  }

  const getTargetValues = (selectedItems) => {
    if (Array.isArray(selectedItems) && selectedItems.length) {
      return selectedItems.map((ele) => ele.id)
    }
    return DROPDOWN_DEFAULT_VALUE
  }

  // const selectedValues = React.useMemo(() => {
  //   return listData?.find((it) => it.id == allValues[getFieldId(field.id)])
  // }, [allValues, listData, teamsList])

  const selectedValues = React.useMemo(() => {
    return listDataSynced?.find((it) => it.id == allValues[getFieldId(field.id)])
  }, [allValues, listDataSynced])

  // const selectedValues = React.useMemo(() => {
  //   const useTeamsList = listDataDep.some(
  //     (it) => it.parent_list_type == 'team' || it.child_list_type == 'team'
  //   )

  //   const sourceArray = useTeamsList ? teamsList : listData

  //   return sourceArray?.find((it) => it.id == allValues[getFieldId(field.id)])
  // }, [allValues, listData, teamsList, listDataDep])

  switch (field.data_structure) {
    case FIELD_TYPES.LIST:
      return (
        <AutoCompleteField
          fieldId={field.id}
          field={field}
          className={getFieldId(field.id)}
          value={selectedValues || null}
          onChange={onChange}
          options={listDataSynced}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          helperText={helperText}
          isPivotDataLoading={importDataLoading}
          allValues={allValues}
          listDataIdMetadataMap={listDataIdMetadataMap}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.LIST_MULTI:
      return (
        <MultiSelectField
          id={field.id}
          field={field}
          allValues={allValues}
          listDataIdMetadataMap={listDataIdMetadataMap}
          className={getFieldId(field.id)}
          helperText={helperText}
          onChange={onChange}
          options={
            syncWithParent(
              field.id,
              fieldTemplateDep,
              listDataDep,
              allValues,
              listData,
              teamsList,
              {
                field: field,
                userTeamMap: userTeamMap,
              },
            ) || listData
          }
          value={allValues[getFieldId(field.id)]}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.FILE:
      return !allValues[getFieldId(field.id)] ? (
        <FileUploadField
          id={field.id}
          className={getFieldId(field.id)}
          onChange={onChange}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          placeholder={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          field={field}
          allValues={allValues}
          helperText={helperText}
          inputSx={inputSx}
        />
      ) : (
        <FilePreviewField
          id={field.id}
          className={getFieldId(field.id)}
          value={allValues[getFieldId(field.id)]}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          download={() => fileServices.downloadFile(user, allValues[getFieldId(field.id)])}
          clear={() => persistAllValueChange({ [getFieldId(field.id)]: '' })}
          disabled={disabled}
          field={field}
          allValues={allValues}
          processContext={processContext}
          helperText={helperText}
          processId={processContext?.processData?.id}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.TABLE:
      return (
        <TableField
          className={getFieldId(field.id)}
          field={field}
          fields={fields}
          disabled={disabled}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.CURRENCY:
      return (
        <CurrencyField
          id={field.id}
          field={field}
          allValues={allValues}
          className={`f_${field.id}`}
          fields={fields}
          value={allValues[getFieldId(field.id)]}
          onChange={onChange}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          helperText={helperText}
          processId={processContext?.processData?.id}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.BIGTEXT:
      return (
        <BigTextField
          id={field.id}
          field={field}
          allValues={allValues}
          className={getFieldId(field.id)}
          value={allValues[getFieldId(field.id)]}
          onChange={onChange}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          helperText={helperText}
          processId={processContext?.processData?.id}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
      return (
        <TimeStampedBigTextField
          id={field.id}
          field={field}
          className={getFieldId(field.id)}
          value={allValues[getFieldId(field.id)] || []}
          onChange={onChange}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          helperText={helperText}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.NUMBER:
      return (
        <TextOrNumberField
          id={field.id}
          field={field}
          allValues={allValues}
          className={getFieldId(field.id)}
          type={field.data_structure}
          onChange={onChange}
          value={allValues[getFieldId(field.id)]}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          disabled={disabled}
          helperText={helperText}
          processId={processContext?.processData?.id}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.DATE:
    case FIELD_TYPES.DATETIME:
      return (
        <DateAndDateTime
          id={field.id}
          className={getFieldId(field.id)}
          value={
            allValues[getFieldId(field.id)]?.length > 0
              ? moment(allValues[getFieldId(field.id)])
              : null
          }
          onChange={onChange}
          label={field?.field_template_name + (field?.is_required ? ' *' : '')}
          type={field?.data_structure}
          disabled={disabled}
          helperText={helperText}
          field={field}
          allValues={allValues}
          processId={processContext?.processData?.id}
          inputSx={inputSx}
        />
      )
    case FIELD_TYPES.CUSTOM_VIEW:
      return (
        <CustomView
          id={field.id}
          config={field.metadata}
          fields={fields}
          tableContext={TABLE_CONTEXTS.DATA_ENTRY_VIEW}
        />
      )
    default:
      return (
        <p style={{ color: 'text.warning' }}>
          {field.data_structure} - Not a valid field to render!!
        </p>
      )
  }
}

export default Fields
